import React, { useState } from "react";

import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { Island } from "@excalidraw/excalidraw/components/Island";
import { TextField } from "@excalidraw/excalidraw/components/TextField";
import { FilledButton } from "@excalidraw/excalidraw/components/FilledButton";

import { useAtom } from "../app-jotai";
import { storageConfigAtom } from "../app-jotai";

export type StorageType = "default" | "kv" | "s3" | "indexed-db";

const StorageSettingsDialog = ({ onClose }: { onClose: () => void }) => {
  const [config, setConfig] = useAtom(storageConfigAtom);
  const [storageType, setStorageType] = useState<StorageType>(config.type);

  // Local state for form inputs
  const [kvUrl, setKvUrl] = useState(config.kvUrl || "");
  const [kvApiToken, setKvApiToken] = useState(config.kvApiToken || "");
  const [s3AccessKeyId, setS3AccessKeyId] = useState(
    config.s3AccessKeyId || "",
  );
  const [s3SecretAccessKey, setS3SecretAccessKey] = useState(
    config.s3SecretAccessKey || "",
  );
  const [s3Region, setS3Region] = useState(config.s3Region || "");
  const [s3BucketName, setS3BucketName] = useState(config.s3BucketName || "");

  const handleSave = () => {
    setConfig({
      type: storageType,
      kvUrl,
      kvApiToken,
      s3AccessKeyId,
      s3SecretAccessKey,
      s3Region,
      s3BucketName,
    });
    onClose();
  };

  const renderForm = () => {
    switch (storageType) {
      case "kv":
        return (
          <>
            <TextField
              label="KV URL"
              value={kvUrl}
              placeholder="你的 Cloudflare KV URL"
              onChange={setKvUrl}
            />
            <TextField
              label="API Token"
              value={kvApiToken}
              placeholder="你的 Cloudflare API Token"
              onChange={setKvApiToken}
            />
          </>
        );
      case "s3":
        return (
          <>
            <TextField
              label="Access Key ID"
              value={s3AccessKeyId}
              placeholder="你的 AWS Access Key ID"
              onChange={setS3AccessKeyId}
            />
            <TextField
              label="Secret Access Key"
              value={s3SecretAccessKey}
              placeholder="你的 AWS Secret Access Key"
              onChange={setS3SecretAccessKey}
            />
            <TextField
              label="Region"
              value={s3Region}
              placeholder="例如 us-east-1"
              onChange={setS3Region}
            />
            <TextField
              label="Bucket Name"
              value={s3BucketName}
              placeholder="你的 S3 Bucket 名称"
              onChange={setS3BucketName}
            />
          </>
        );
      case "indexed-db":
        return (
          <p>
            画布会安全保存在当前浏览器的本地数据库中，不会绑定账号，也不会在线同步。
          </p>
        );
      case "default":
      default:
        return (
          <p>
            画布会保存到当前自托管 Excalidraw 的默认后端，并绑定到你的登录账号。
            退出登录后将不会显示账号画布。
          </p>
        );
    }
  };

  return (
    <Dialog
      onCloseRequest={onClose}
      title={"数据源设置"}
      className="storage-settings-dialog"
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <p>
          安全提醒：敏感密钥只保存在当前浏览器的会话存储中，关闭标签页后会被清除。
        </p>

        <select
          value={storageType}
          onChange={(e) => setStorageType(e.target.value as StorageType)}
          style={{
            padding: "0.5rem",
            borderRadius: "var(--border-radius-lg)",
            border: "1px solid var(--color-border-outline)",
          }}
        >
          <option value="default">默认后端（在线同步）</option>
          <option value="indexed-db">浏览器本地（IndexedDB）</option>
          <option value="kv">Cloudflare KV（在线同步）</option>
          <option value="s3">Amazon S3（在线同步）</option>
        </select>

        <Island style={{ padding: "1rem" }}>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
          >
            {renderForm()}
          </div>
        </Island>

        <div
          style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}
        >
          <FilledButton color="primary" label={"保存"} onClick={handleSave} />
        </div>
      </div>
    </Dialog>
  );
};

export default StorageSettingsDialog;
